import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Modal, Input, VbButton } from '@/components/vb'
import { projectsInstance } from '@/services/ProjectsService'
import { useAuth } from '@/context/AuthContext'
import { useWorkspace } from '@/context/WorkspaceContext'
import { toast } from 'sonner'

interface ShareReportDialogProps {
    reportId: number
    companyId?: number | null
    companyName?: string | null
    open: boolean
    onOpenChange: (open: boolean) => void
    trigger: React.ReactNode
}

/** Share-report dialog, on the v2 language (AITS-493): the shared `Modal`
 *  (v2 scrim + radius-lg + shadow-xl), the design-system `Input`, and `VbButton`
 *  replace the shadcn Dialog/Input/Button. The `trigger` stays owned by the
 *  caller — with `Modal` there is no `DialogTrigger`, so it's cloned to open the
 *  controlled dialog on click (the caller drives `open`/`onOpenChange`). */
export const ShareReportDialog: React.FC<ShareReportDialogProps> = ({
                                                                        reportId,
                                                                        companyId,
                                                                        companyName,
                                                                        open,
                                                                        onOpenChange,
                                                                        trigger,
                                                                    }) => {
    const { token } = useAuth()
    const { activeWorkspace } = useWorkspace()
    const [email, setEmail] = useState('')
    const [list, setList] = useState<string[]>([])
    const [loading, setLoading] = useState(false)

    const addEmail = () => {
        const e = email.trim()
        if (e && !list.includes(e)) {
            setList(all => [...all, e])
            setEmail('')
        }
    }

    const removeEmail = (e: string) => {
        setList(all => all.filter(x => x !== e))
    }

    const onSubmit = async () => {
        if (list.length === 0) {
            toast.error('Add at least one recipient')
            return
        }
        setLoading(true)
        try {
            await projectsInstance.shareReport(
                reportId,
                list,
                token,
                activeWorkspace?.id,
                companyId,
                companyName,
            )
            toast.success('Report sent!')
            onOpenChange(false)
            setList([])
        } catch (err: any) {
            console.error(err)
            toast.error(err.message || 'Failed to send')
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {React.isValidElement(trigger)
                ? React.cloneElement(trigger as React.ReactElement<any>, {
                      onClick: (e: React.MouseEvent) => {
                          (trigger as React.ReactElement<any>).props.onClick?.(e)
                          onOpenChange(true)
                      },
                  })
                : trigger}

            <Modal
                open={open}
                onOpenChange={onOpenChange}
                title="Share report"
                widthClassName="max-w-md"
                footer={
                    <VbButton onClick={onSubmit} disabled={loading} className="w-full">
                        {loading ? 'Sending…' : 'Send'}
                    </VbButton>
                }
            >
                <div className="flex flex-col gap-4">
                    <div className="flex gap-2">
                        <Input
                            placeholder="email@example.com"
                            value={email}
                            onChange={e => setEmail(e.currentTarget.value)}
                            onKeyDown={e => e.key === 'Enter' && addEmail()}
                        />
                        <VbButton variant="secondary" onClick={addEmail} disabled={!email.trim()}>
                            Add
                        </VbButton>
                    </div>

                    {list.length > 0 && (
                        <ul className="flex flex-col gap-1 max-h-32 overflow-auto">
                            {list.map(e => (
                                <li
                                    key={e}
                                    className="flex justify-between items-center gap-2 bg-vb-bg-subtle px-2.5 py-1.5 rounded-vb-sm"
                                >
                                    <span className="text-sm text-vb-text-2 truncate">{e}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeEmail(e)}
                                        aria-label={`Remove ${e}`}
                                        className="grid h-5 w-5 shrink-0 place-items-center rounded-vb-xs text-vb-text-3 transition-colors hover:bg-vb-fail-bg hover:text-vb-fail-ink"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>
        </>
    )
}
